import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { RootState } from '../index'

export interface Humidor {
  id: string
  userId: string
  name: string
  capacity: number
  location?: string
  temperatureTarget?: number
  humidityTarget?: number
  currentTemperature?: number
  currentHumidity?: number
  sensorDeviceId?: string
  notes?: string
  isArchived?: boolean
  archivedAt?: string
  lastAccessed?: string
  utilizationRate?: number
  modifiedBy?: string
  createdAt: string
  updatedAt: string
  cigarCount: number
  capacityUsage: number
  _count?: {
    cigars: number
  }
}

export interface CreateHumidorRequest {
  name: string
  capacity: number
  location?: string
  temperatureTarget?: number
  humidityTarget?: number
  notes?: string
}

export interface UpdateHumidorRequest {
  name?: string
  capacity?: number
  location?: string
  temperatureTarget?: number
  humidityTarget?: number
  sensorDeviceId?: string
  notes?: string
}

export interface ArchiveHumidorRequest {
  id: string
  archive: boolean
}

export interface BulkOperationsRequest {
  humidorIds: string[]
  operation: 'archive' | 'unarchive' | 'delete'
}

export interface UpdateUtilizationRequest {
  id: string
  utilizationRate: number
}

export interface HumidorCapacity {
  totalCapacity: number
  usedCapacity: number
  availableCapacity: number
  usagePercentage: number
}

interface ApiResponse<T> {
  success: boolean
  message: string
  data: T
}

interface HumidorsResponse {
  humidors: Humidor[]
}

interface HumidorResponse {
  humidor: Humidor
}

export const humidorApi = createApi({
  reducerPath: 'humidorApi',
  baseQuery: fetchBaseQuery({
    baseUrl: '/api/v1/humidors',
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token
      if (token) {
        headers.set('authorization', `Bearer ${token}`)
      }
      return headers
    },
  }),
  tagTypes: ['Humidor'],
  endpoints: (builder) => ({
    getHumidors: builder.query<Humidor[], void>({
      query: () => '',
      transformResponse: (response: ApiResponse<HumidorsResponse>) => {
        // Extract humidors array from the nested response structure
        return response.data?.humidors || []
      },
      providesTags: ['Humidor'],
    }),
    getHumidor: builder.query<Humidor, string>({
      query: (id) => `/${id}`,
      transformResponse: (response: ApiResponse<HumidorResponse>) => {
        // Extract humidor from the nested response structure
        return response.data?.humidor || response.data
      },
      providesTags: (_result, _error, id) => [{ type: 'Humidor', id }],
    }),
    createHumidor: builder.mutation<Humidor, CreateHumidorRequest>({
      query: (humidor) => ({
        url: '',
        method: 'POST',
        body: humidor,
      }),
      transformResponse: (response: ApiResponse<HumidorResponse>) => {
        return response.data?.humidor || response.data
      },
      invalidatesTags: ['Humidor'],
    }),
    updateHumidor: builder.mutation<Humidor, { id: string; updates: UpdateHumidorRequest }>({
      query: ({ id, updates }) => ({
        url: `/${id}`,
        method: 'PUT',
        body: updates,
      }),
      transformResponse: (response: ApiResponse<HumidorResponse>) => {
        return response.data?.humidor || response.data
      },
      invalidatesTags: ['Humidor'],
    }),
    deleteHumidor: builder.mutation<void, string>({
      query: (id) => ({
        url: `/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Humidor'],
    }),
    getHumidorCapacity: builder.query<HumidorCapacity, string>({
      query: (id) => `/${id}/capacity`,
      providesTags: (_result, _error, id) => [{ type: 'Humidor', id, capacity: true }],
    }),
    updateHumidorConditions: builder.mutation<Humidor, { id: string; temperature?: number; humidity?: number }>({
      query: ({ id, temperature, humidity }) => ({
        url: `/${id}/conditions`,
        method: 'PUT',
        body: { temperature, humidity },
      }),
      transformResponse: (response: ApiResponse<HumidorResponse>) => {
        return response.data?.humidor || response.data
      },
      invalidatesTags: ['Humidor'],
    }),
    // Archive/unarchive humidor
    archiveHumidor: builder.mutation<Humidor, ArchiveHumidorRequest>({
      query: ({ id, archive }) => ({
        url: `/${id}/archive`,
        method: 'PUT',
        body: { archive },
      }),
      transformResponse: (response: ApiResponse<HumidorResponse>) => {
        return response.data?.humidor || response.data
      },
      invalidatesTags: ['Humidor'],
    }),
    // Bulk operations
    bulkOperations: builder.mutation<
      { operation: string; affectedCount: number; requestedCount: number },
      BulkOperationsRequest
    >({
      query: (request) => ({
        url: '/bulk',
        method: 'POST',
        body: request,
      }),
      invalidatesTags: ['Humidor'],
    }),
    // Update utilization rate
    updateUtilization: builder.mutation<Humidor, UpdateUtilizationRequest>({
      query: ({ id, utilizationRate }) => ({
        url: `/${id}/utilization`,
        method: 'PUT',
        body: { utilizationRate },
      }),
      transformResponse: (response: ApiResponse<HumidorResponse>) => {
        return response.data?.humidor || response.data
      },
      invalidatesTags: ['Humidor'],
    }),
  }),
})

export const {
  useGetHumidorsQuery,
  useGetHumidorQuery,
  useCreateHumidorMutation,
  useUpdateHumidorMutation,
  useDeleteHumidorMutation,
  useGetHumidorCapacityQuery,
  useUpdateHumidorConditionsMutation,
  useArchiveHumidorMutation,
  useBulkOperationsMutation,
  useUpdateUtilizationMutation,
} = humidorApi
